import type { CreateKnowledgeBaseData, KnowledgeBase, KnowledgeBaseFilters, KnowledgeDocument, UpdateKnowledgeBaseData } from "@/types/knowledge-base";

// Mock Knowledge Bases
let mockKnowledgeBases: KnowledgeBase[] = [
    {
        knowledge_base_id: "1",
        name: "General Logistics Knowledge",
        description: "General knowledge base for logistics and shipping operations",
        kb_type: "general",
        document_count: 45,
        embedding_model: "text-embedding-3-small",
        dimension: 1536,
        is_active: true,
        last_indexed_at: new Date("2024-01-15").toISOString(),
        created_at: new Date("2024-01-01").toISOString(),
        updated_at: new Date("2024-01-15").toISOString(),
    },
    {
        knowledge_base_id: "2",
        name: "Trade Documents Library",
        description: "Collection of trade documents and their specifications",
        kb_type: "documents",
        document_count: 128,
        embedding_model: "text-embedding-3-small",
        dimension: 1536,
        is_active: true,
        last_indexed_at: new Date("2024-01-20").toISOString(),
        created_at: new Date("2024-01-05").toISOString(),
        updated_at: new Date("2024-01-20").toISOString(),
    },
    {
        knowledge_base_id: "3",
        name: "Shipping Routes Database",
        description: "Information about shipping routes, ports, and transit times",
        kb_type: "routes",
        document_count: 67,
        embedding_model: "text-embedding-3-small",
        dimension: 1536,
        is_active: true,
        last_indexed_at: new Date("2024-01-18").toISOString(),
        created_at: new Date("2024-01-10").toISOString(),
        updated_at: new Date("2024-01-18").toISOString(),
    },
    {
        knowledge_base_id: "4",
        name: "Trade Regulations",
        description: "International trade regulations and compliance requirements",
        kb_type: "regulation",
        document_count: 92,
        embedding_model: "text-embedding-3-small",
        dimension: 1536,
        is_active: true,
        last_indexed_at: new Date("2024-01-22").toISOString(),
        created_at: new Date("2024-01-12").toISOString(),
        updated_at: new Date("2024-01-22").toISOString(),
    },
];

// Mock Knowledge Documents
let mockKnowledgeDocuments: KnowledgeDocument[] = [
    {
        doc_id: "doc1",
        knowledge_base_id: "1",
        knowledge_base_name: "General Logistics Knowledge",
        title: "Introduction to Container Shipping",
        category: ["shipping", "containers"],
        topic: ["basics", "introduction"],
        priority: 5,
        tags: ["container", "shipping", "logistics"],
        language: "en",
        access_level: "company",
        retrieval_count: 45,
        chunk_count: 12,
        created_at: new Date("2024-01-02").toISOString(),
    },
    {
        doc_id: "doc2",
        knowledge_base_id: "2",
        knowledge_base_name: "Trade Documents Library",
        title: "Bill of Lading Specifications",
        category: ["documents", "bills"],
        topic: ["bill-of-lading", "specifications"],
        priority: 5,
        tags: ["bill-of-lading", "documents"],
        language: "en",
        access_level: "company",
        retrieval_count: 32,
        chunk_count: 8,
        created_at: new Date("2024-01-06").toISOString(),
    },
];

// Knowledge Base Service
export const knowledgeBaseService = {
    getAll: (): KnowledgeBase[] => {
        return [...mockKnowledgeBases];
    },

    getById: (id: string): KnowledgeBase | undefined => {
        return mockKnowledgeBases.find((kb) => kb.knowledge_base_id === id);
    },

    search: (filters: KnowledgeBaseFilters): KnowledgeBase[] => {
        let results = [...mockKnowledgeBases];

        if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            results = results.filter((kb) => kb.name.toLowerCase().includes(searchLower) || kb.description?.toLowerCase().includes(searchLower));
        }

        if (filters.kb_type) {
            results = results.filter((kb) => kb.kb_type === filters.kb_type);
        }

        return results;
    },

    create: (data: CreateKnowledgeBaseData): KnowledgeBase => {
        const newKB: KnowledgeBase = {
            knowledge_base_id: (mockKnowledgeBases.length + 1).toString(),
            name: data.name,
            description: data.description,
            kb_type: data.kb_type,
            document_count: 0,
            embedding_model: "text-embedding-3-small",
            dimension: 1536,
            is_active: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
        };
        mockKnowledgeBases.push(newKB);
        return newKB;
    },

    update: (id: string, data: UpdateKnowledgeBaseData): KnowledgeBase | null => {
        const index = mockKnowledgeBases.findIndex((kb) => kb.knowledge_base_id === id);
        if (index === -1) return null;

        mockKnowledgeBases[index] = {
            ...mockKnowledgeBases[index],
            ...data,
            updated_at: new Date().toISOString(),
        };
        return mockKnowledgeBases[index];
    },

    delete: (id: string): boolean => {
        const index = mockKnowledgeBases.findIndex((kb) => kb.knowledge_base_id === id);
        if (index === -1) return false;

        mockKnowledgeBases.splice(index, 1);
        return true;
    },
};

// Knowledge Document Service
export const knowledgeDocumentService = {
    getByKnowledgeBaseId: (kbId: string): KnowledgeDocument[] => {
        return mockKnowledgeDocuments.filter((doc) => doc.knowledge_base_id === kbId);
    },

    getById: (id: string): KnowledgeDocument | undefined => {
        return mockKnowledgeDocuments.find((doc) => doc.doc_id === id);
    },

    upload: async (kbId: string, file: File): Promise<KnowledgeDocument> => {
        // Simulate upload
        const newDoc: KnowledgeDocument = {
            doc_id: `doc${mockKnowledgeDocuments.length + 1}`,
            knowledge_base_id: kbId,
            title: file.name,
            category: [],
            topic: [],
            priority: 3,
            tags: [],
            language: "en",
            access_level: "company",
            retrieval_count: 0,
            chunk_count: 0,
            created_at: new Date().toISOString(),
        };
        mockKnowledgeDocuments.push(newDoc);

        // Update KB document count
        const kb = knowledgeBaseService.getById(kbId);
        if (kb) {
            const docs = knowledgeDocumentService.getByKnowledgeBaseId(kbId);
            knowledgeBaseService.update(kbId, { document_count: docs.length });
        }

        return newDoc;
    },

    delete: (id: string): boolean => {
        const index = mockKnowledgeDocuments.findIndex((doc) => doc.doc_id === id);
        if (index === -1) return false;

        const doc = mockKnowledgeDocuments[index];
        mockKnowledgeDocuments.splice(index, 1);

        // Update KB document count
        const kb = knowledgeBaseService.getById(doc.knowledge_base_id);
        if (kb) {
            const docs = knowledgeDocumentService.getByKnowledgeBaseId(doc.knowledge_base_id);
            knowledgeBaseService.update(doc.knowledge_base_id, { document_count: docs.length });
        }

        return true;
    },
};
